/* eslint-disable */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable max-len */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Arrow from 'src/assets/arrow-left-black.svg';

import {
  CommonDropdown,
  CommonInput,
  FormErrorWrap,
  RegularButton,
  ICommonDropdownOption,
  FullScreenLoading,
  OutlinedButton,
  DatePicker,
} from 'pg-ui-library';
import {
  Container,
  CardContainer,
  HelpForm,
  PageTitle,
  FormInputWrapper,
  NavTextLink,
  ArrowBack,
  HeaderItemLeft,
  ModuleLine,
  SearchLink,
  SubText,
} from './createStyles';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { supportDeskOptions, supportDeskSubmodules } from 'src/modules/personal-info';
import { useAddInvoice } from './mutations/invoice.mutation';
import { InvoiceFormValues, AssignmentApiItem } from 'src/interfaces/invoice';
import { Link } from 'react-router-dom';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useQuery } from '@tanstack/react-query';
import InvoiceService from 'src/services/invoice.service';

export const toISODate = (date: string | Date): string => {
  if (date instanceof Date) return date.toISOString();
  return new Date(date).toISOString();
};

const formatDateOnly = (date: Date | null | undefined): string => {
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const toDateOnly = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  if (typeof date === 'string') {
    // If the string is already YYYY-MM-DD, return it directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    } // For ISO strings, take only the date portion.
    return date.split('T')[0];
  } // IMPORTANT: // Use local date getters instead of toISOString(), // because toISOString() converts the date to UTC.
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CreateInvoice: React.FC = () => {
  const [showFun, setShowFun] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<ICommonDropdownOption[]>(
    supportDeskSubmodules[supportDeskOptions[0].value]
  );

  const [contractStartDate, setContractStartDate] = useState<Date | null>(null);
  const [contractEndDate, setContractEndDate] = useState<Date | null>(null);

  const [userRole] = useLocalStorage('userRole', null);

  useEffect(() => {
    if (userRole === 'external_manager') {
      setShowFun(false);
    } else {
      setShowFun(true);
    }
  }, [userRole]);

  const addInvoice = useAddInvoice();
  const navigate = useNavigate();
  const {
    isFetching: isEmployeeFetching,
    data: employeeData,
    refetch: employeeRefetch,
  } = useQuery(['employee-detail'], async () => {
    const result = await InvoiceService.getEmployee();
    return result?.data?.employee;
  });

  const {
    isFetching: isAssignmentFetching,
    data: assignmentData,
    refetch: assignmentRefetch,
  } = useQuery(['assignment-list-personnal'], async () => {
    const result = await InvoiceService.getAssignmentsPersonalNumber();
    return result?.data?.vw_msd_jd_assignments;
  });

  const formatDate = (date?: string | null) => {
    if (!date) return 'Open';
    return date.split('T')[0];
  };

  const assignmentOptions: ICommonDropdownOption[] = useMemo(() => {
    if (!assignmentData?.items) return [];

    return (
      assignmentData.items.map((item: AssignmentApiItem) => ({
        key: item.ASSIGNMENT_ID,
        value: item.ASSIGNMENT_ID,
        label: `${item.JOBTITLE} (${item.ASSIGNMENT_ID}) - ${item.CLIENT_NAME} (${formatDate(
          item.CONTRACT_START_DATE
        )} - ${formatDate(item.CONTRACT_END_DATE)})`,
      })) ?? []
    );
  }, [assignmentData]);

  const assignmentOptionsWithBlank: ICommonDropdownOption[] = [
    {
      key: '',
      label: 'Select',
      value: '',
    },
    ...assignmentOptions,
  ];

  useEffect(() => {
    if (!assignmentData?.items?.length) return;
    if (formik.values.assignmentId) return;

    if (assignmentOptionsWithBlank) {
    } else {
      const first = assignmentData.items[0];

      formik.setValues(prev => ({
        ...prev,
        assignmentId: first.ASSIGNMENT_ID,
        assignmentName: first.JOBTITLE,
        workerId: first.EMPLOYEEID,

        supplierName: '',
        jobTitle: first.JOBTITLE,
        hireType: first.HIRETYPE,
        billRate: String(first.BILL_RATE ?? ''),
        payRate: String(first.PAY_RATE ?? ''),

        rateMarkup: String(first.MARKUP_PERCENT ?? ''),
        approverName: first.ASSIGNMENT_APPROVER_NAME,
        approverEmail: first.APPROVER_EMAIL,

        clientId: first.COMPANY_ID,
        clientName: first.CLIENT_NAME,

        companyName: first.COMPANY_NAME,

        employmentCategory: first.EMPLOYMENT_CATEGORY ?? null,
        billingContactName: first.BILLING_CONTACT ?? null,
        workingCity: first.WORKING_CITY ?? null,
        workingState: first.WORKING_STATE ?? null,
        address1: first.ADDRESS1 ?? null,

        payOvertimeExempt: first.PAY_OVERTIME_EXEMPT ?? null,

        overtimePayRate: first.OVERTIME_PAY_RATE !== undefined ? parseFloat(first.OVERTIME_PAY_RATE) : null,

        hoursPerWeek: first.HOURSPERWEEK !== undefined ? parseFloat(first.HOURSPERWEEK) : null,

        contractStartDate: first.CONTRACT_START_DATE || null,
        contractEndDate: first.CONTRACT_END_DATE || null,

        managerName: first.MANAGER_NAME ?? null,
        managerEmail: first.MANAGER_EMAIL ?? null,

        coApproverName: first.BACKUP_APPROVER_NAME ?? null,
        coApproverEmail: first.BACKUP_APPROVER_EMAIL ?? null,

        assignmentType: first.ASSIGNMENT_TYPE ?? null,
        workerPayCompany: first.WORKER_PAY_COMPANY ?? null,
        payModel: first.PAY_MODEL ?? null,

        expensesEnabled: first.EXPENSES_ENABLED === '1',

        candidateCity: first.CANDIDATE_CITY ?? null,
        candidateState: first.CANDIDATE_STATE ?? null,

        currency: first.CURRENCY,

        // latest fields

        divison: first.DIVISION ?? '',
        overtime: first.OVERTIME ?? '',
        otCalculation: first.OT_CALCULATION ?? '',
        overtimeEligibility: first.OVERTIMEELIGIBILITY ?? '',

        markupPercent: first.MARKUP_PERCENT ?? '',
        w2Type: first.W2_TYPE ?? '',
        workMode: first.WORKMODE ?? '',

        costCenterName: first.COST_CENTER_NAME ?? '',

        department: first.Departments ?? '',

        billingaddress: first.BILLING_ADDRESS1 ?? '',
        billingAddress2: first.BILLING_ADDRESS2 ?? '',

        billingCity: first.BILLING_CITY ?? '',
        billingState: first.BILLING_STATE ?? '',
        billingCountry: first.BILLING_COUNTRY ?? '',
        assignmentStatus: first.ASSIGNMENT_STATUS ?? '',

        postalCode: first.POSTAL_CODE ?? '',

        payCost: first.PAY_COST ?? '',
        projectCode: first.PROJECT_CODE ?? '',
        costCenter: first.COST_CENTER ?? '',

        billAddressSameAsWorkAddress: first.BILL_ADDRESS_SAME_AS_WORK_ADDRESS === '1',
        billingPostalCOde: first.BILLING_ZIP ?? '',
        glCode: first.GL_CODE ?? '',
        externalReferenceId: first.VMS_ID ?? '',
      }));
    }

    // 🔑 THIS prevents "Assignment is required" on load
    formik.setFieldTouched('assignmentId', false);
  }, [assignmentData]);

  const validationSchema = yup.object({
    assignmentId: yup.string().max(255).required('Assignment is required'),
    invoiceDate: yup.date().nullable().required('Invoice date is required'),
    periodStart: yup.date().nullable().required('Period Start is required'),
    periodEnd: yup.date().nullable().required('Period End is required'),
    invoiceAmount: yup.string().required('Invoice Amount is required'),
    totalUnits: yup.string().required('Total Units is required'),
    // attachments: yup.array().min(1, 'Attachments is required'),
    attachments: yup.array().min(1, 'At least one attachment is required'),
  });

  const formik = useFormik({
    initialValues: {
      workerId: '',
      assignmentId: '',
      assignmentName: '',
      supplierName: '',
      invoiceNumber: '',
      jobTitle: '',
      hireType: '',
      invoiceDate: null,
      periodStart: null,
      periodEnd: null,
      invoiceAmount: '',
      currency: '',
      totalUnits: '',
      totalHours: '',
      billRate: '',
      payRate: '',
      rateMarkup: '',
      status: 'Submitted',
      attachments: [],
      approverEmail: '',
      approverName: '',
      notes: '',

      // ✅ NEW FIELDS
      clientId: '',
      clientName: '',
      companyName: '',
      employmentCategory: '',
      billingContactName: '',
      workingCity: '',
      workingState: '',
      address1: '',
      payOvertimeExempt: '',
      overtimePayRate: '',
      hoursPerWeek: '',
      contractStartDate: null,
      contractEndDate: null,
      managerName: '',
      managerEmail: '',
      coApproverName: '',
      coApproverEmail: '',
      assignmentType: '',
      workerPayCompany: '',
      payModel: '',
      expensesEnabled: false,
      candidateCity: '',
      candidateState: '',
      postalCode: '',
      billAddressSameAsWorkAddress: false,
      payCost: '',
      projectCode: '',
      costCenter: '',
      department: '',
      merId: '',
      purchaseOrder: '',
      // latest fields
      divison: '',
      overtime: '',
      otCalculation: '',
      overtimeEligibility: '',

      markupPercent: '',
      w2Type: '',
      workMode: '',

      costCenterId: '',
      costCenterName: '',

      billingaddress: '',
      billingAddress2: '',

      billingCity: '',
      billingState: '',
      billingCountry: '',
      // projectCode:'',
      assignmentStatus: '',
      billingPostalCOde: '',
      glCode: '',
      externalReferenceId: '',
    },
    validationSchema,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values: InvoiceFormValues) => {
      try {
        const formData = new FormData();
        formData.append('assignmentId', values.assignmentId || '');
        formData.append('assignmentName', values.assignmentName || '');
        formData.append('clientName', values.clientName || '');
        formData.append('supplierName', values.supplierName || '');
        // formData.append('invoiceDate', values.invoiceDate ? toISODate(values.invoiceDate) : '');
        // formData.append( 'invoiceDate', values.invoiceDate ? toDateOnly(values.invoiceDate) : '' );
        formData.append('invoiceDate', formatDateOnly(values.invoiceDate));
        formData.append('invoiceNumber', values.invoiceNumber || '');
        formData.append('jobTitle', values.jobTitle || '');
        formData.append('hireType', values.hireType || '');
        // formData.append('periodStart', values.periodStart ? toISODate(values.periodStart) : '');
        // formData.append( 'periodStart', values.periodStart ? toDateOnly(values.periodStart) : '' );
        // formData.append('periodEnd', values.periodEnd ? toISODate(values.periodEnd) : '');
        // formData.append( 'periodEnd', values.periodEnd ? toDateOnly(values.periodEnd) : '' );

        formData.append('periodStart', formatDateOnly(values.periodStart));

        formData.append('periodEnd', formatDateOnly(values.periodEnd));
        formData.append('status', values.status ? values.status : '');
        formData.append('invoiceAmount', values.invoiceAmount || '');
        formData.append('totalUnits', values.totalUnits || '');
        formData.append('totalHours', values.totalHours || '');
        formData.append('billRate', values.billRate || '');
        formData.append('payRate', values.payRate || '');
        formData.append('rateMarkup', values.rateMarkup || '');
        formData.append('approverEmail', values.approverEmail || '');
        formData.append('approverName', values.approverName || '');
        formData.append('notes', values.notes || '');
        formData.append('currency', values.currency || '');

        // --- NEW FIELDS
        formData.append('companyName', values.companyName || '');
        formData.append('clientId', values.companyId || '');
        formData.append('employmentCategory', values.employmentCategory || '');
        formData.append('billingContactName', values.billingContactName || '');
        formData.append('workingCity', values.workingCity || '');
        formData.append('workingState', values.workingState || '');
        formData.append('address1', values.address1 || '');

        formData.append('payOvertimeExempt', values.payOvertimeExempt || '');

        formData.append('overtimePayRate', values.overtimePayRate ? String(values.overtimePayRate) : '');

        formData.append('hoursPerWeek', values.hoursPerWeek ? String(values.hoursPerWeek) : '');

        // formData.append('contractStartDate', values.contractStartDate ? toISODate(values.contractStartDate) : '');
        formData.append('contractStartDate', values.contractStartDate ? toDateOnly(values.contractStartDate) : '');

        // formData.append('contractEndDate', values.contractEndDate ? toISODate(values.contractEndDate) : '');
        formData.append('contractEndDate', values.contractEndDate ? toDateOnly(values.contractEndDate) : '');

        formData.append('managerName', values.managerName || '');
        formData.append('managerEmail', values.managerEmail || '');
        formData.append('coApproverName', values.coApproverName || '');
        formData.append('coApproverEmail', values.coApproverEmail || '');

        formData.append('assignmentType', values.assignmentType || '');
        formData.append('workerPayCompany', values.workerPayCompany || '');
        formData.append('payModel', values.payModel || '');

        formData.append('expensesEnabled', values.expensesEnabled ? '1' : '0');

        formData.append('candidateCity', values.candidateCity || '');
        formData.append('candidateState', values.candidateState || '');

        formData.append('postalCode', values.postalCode || '');

        formData.append('billAddressSameAsWorkAddress', values.billAddressSameAsWorkAddress ? '1' : '0');

        formData.append('payCost', values.payCost ? String(values.payCost) : '');

        formData.append('projectCode', values.projectCode || '');
        // formData.append('costCenter', values.costCenter || '');
        formData.append('department', String(values.department || ''));
        formData.append('merId', values.merId || '');
        formData.append('purchaseOrder', values.purchaseOrder || '');

        formData.append('divison', values.divison || '');
        formData.append('overtime', values.overtime || '');
        formData.append('otCalculation', values.otCalculation || '');
        formData.append('overtimeEligibility', values.overtimeEligibility || '');

        formData.append('markupPercent', values.markupPercent || '');
        formData.append('w2Type', values.w2Type || '');
        formData.append('workMode', values.workMode || '');

        formData.append('costCenterId', values.costCenterId || '');
        formData.append('costCenter', values.costCenterName || '');

        formData.append('billingaddress', values.billingaddress || '');
        formData.append('billingAddress2', values.billingAddress2 || '');

        formData.append('billingCity', values.billingCity || '');
        formData.append('billingState', values.billingState || '');
        formData.append('billingCountry', values.billingCountry || '');
        formData.append('assignmentStatus', values.assignmentStatus || '');
        formData.append('billingPostalCOde', values.billingPostalCOde || '');
        formData.append('glCode', values.glCode || '');
        formData.append('externalReferenceId', values.externalReferenceId || '');

        uploadedFiles.forEach(file => {
          formData.append('attachments', file);
        });

        await addInvoice.mutateAsync({ invoice: formData });

        navigate(`/invoices`);
      } catch (error) {
        console.error('Error during submission of Ticket', error);
      }
    },
  });

  const fileInputRef = React.useRef<any>(null);
  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current?.click();
    }
  };

  const handleFiles = (files: File[]) => {
    const updatedFiles = [...uploadedFiles, ...files];

    setUploadedFiles(updatedFiles);

    // Update Formik attachments
    formik.setFieldValue('attachments', updatedFiles);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    handleFiles(files);
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer.files || []);

    handleFiles(files);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);

    setUploadedFiles(updatedFiles);

    formik.setFieldValue('attachments', updatedFiles);
  };

  const handleAssignmentChange = (option: string) => {
    const selected = assignmentData?.items?.find((a: AssignmentApiItem) => a.ASSIGNMENT_ID === option);

    console.log(assignmentData, '-', option);
    if (!selected) return;

    // Parse the string date
    const parsedStartDate = selected.CONTRACT_START_DATE ? new Date(selected.CONTRACT_START_DATE) : null;
    const parsedEndDate = selected.CONTRACT_END_DATE ? new Date(selected.CONTRACT_END_DATE) : null;

    setContractStartDate(parsedStartDate);
    setContractEndDate(parsedEndDate);

    formik.setValues(prev => ({
      ...prev,
      assignmentId: selected.ASSIGNMENT_ID,
      assignmentName: selected.JOBTITLE,
      workerId: selected.EMPLOYEEID,
      supplierName: '',
      jobTitle: selected.JOBTITLE,
      hireType: selected.HIRETYPE,
      billRate: String(selected.BILL_RATE ?? ''),
      payRate: String(selected.PAY_RATE ?? ''),
      rateMarkup: String(selected.MARKUP_PERCENT ?? ''),
      approverName: selected.ASSIGNMENT_APPROVER_NAME,
      approverEmail: selected.APPROVER_EMAIL,
      currency: selected.CURRENCY,

      // ✅ NEW FIELDS
      clientId: selected.COMPANY_ID,
      clientName: selected.CLIENT_NAME,
      companyName: selected.COMPANY_NAME,
      employmentCategory: selected.EMPLOYMENT_CATEGORY ?? '',
      billingContactName: selected.BILLING_CONTACT ?? '',
      workingCity: selected.WORKING_CITY ?? '',
      workingState: selected.WORKING_STATE ?? '',
      address1: selected.ADDRESS1 ?? '',

      payOvertimeExempt: selected.PAY_OVERTIME_EXEMPT ?? '',

      overtimePayRate: selected.OVERTIME_PAY_RATE !== undefined ? parseFloat(selected.OVERTIME_PAY_RATE) : '',

      hoursPerWeek: selected.HOURSPERWEEK !== undefined ? parseFloat(selected.HOURSPERWEEK) : '',

      // contractStartDate: selected.CONTRACT_START_DATE || null,
      // contractEndDate: selected.CONTRACT_END_DATE || null,

      contractStartDate: parsedStartDate,
      contractEndDate: parsedEndDate,

      periodStart: null,
      periodEnd: null,

      managerName: selected.MANAGER_NAME ?? '',
      managerEmail: selected.MANAGER_EMAIL ?? '',

      coApproverName: selected.BACKUP_APPROVER_NAME ?? '',
      coApproverEmail: selected.BACKUP_APPROVER_EMAIL ?? '',

      assignmentType: selected.ASSIGNMENT_TYPE ?? '',
      workerPayCompany: selected.WORKER_PAY_COMPANY ?? '',
      payModel: selected.PAY_MODEL ?? '',

      expensesEnabled: selected.EXPENSES_ENABLED === '1',

      candidateCity: selected.CANDIDATE_CITY ?? '',
      candidateState: selected.CANDIDATE_STATE ?? '',

      merId: selected.MERID ?? '',
      // letets fields
      divison: selected.DIVISION ?? '',
      overtime: selected.OVERTIME ?? '',
      otCalculation: selected.OT_CALCULATION ?? '',
      overtimeEligibility: selected.OVERTIMEELIGIBILITY ?? '',

      markupPercent: selected.MARKUP_PERCENT ?? '',
      w2Type: selected.W2_TYPE ?? '',
      workMode: selected.WORKMODE ?? '',

      costCenterId: selected.COST_CENTER_ID ?? '',
      costCenterName: selected.COST_CENTER_NAME ?? '',

      department: String(selected.Departments ?? ''),

      billingaddress: selected.BILLING_ADDRESS1 ?? '',
      billingAddress2: selected.BILLING_ADDRESS2 ?? '',

      billingCity: selected.BILLING_CITY ?? '',
      billingState: selected.BILLING_STATE ?? '',
      billingCountry: selected.BILLING_COUNTRY ?? '',

      assignmentStatus: selected.ASSIGNMENT_STATUS ?? '',

      postalCode: selected.POSTAL_CODE ?? '',

      payCost: selected.PAY_COST ?? '',
      projectCode: selected.PROJECT_CODE ?? '',
      costCenter: selected.COST_CENTER ?? '',

      billAddressSameAsWorkAddress: selected.BILL_ADDRESS_SAME_AS_WORK_ADDRESS === '1',

      billingPostalCOde: selected.BILLING_ZIP ?? '',
      glCode: selected.GL_CODE ?? '',
      externalReferenceId: selected.VMS_ID ?? '',
    }));
  };

  const showError = (name: keyof InvoiceFormValues) =>
    !!formik.errors[name] && (formik.touched[name] || formik.submitCount > 0);

  const today = new Date();
  const effectiveMaxDate = contractEndDate && contractEndDate < today ? contractEndDate : today;

  return (
    <Container>
      {isAssignmentFetching && <FullScreenLoading />}
      <SearchLink>
        <Link to={`/invoices`}>Search Invoices</Link>
      </SearchLink>
      <HeaderItemLeft>
        <NavTextLink to={-1 as any}>
          <ArrowBack src={Arrow} />
        </NavTextLink>
        <PageTitle>Create Invoice</PageTitle>
      </HeaderItemLeft>
      {employeeData ? <SubText>{`${employeeData?.firstName} ${employeeData?.lastName}`}</SubText> : null}

      <CardContainer>
        <HelpForm>
          <FormInputWrapper style={{ flex: '0 0 100%', marginBottom: '-10px' }}>
            {showFun ? (
              ''
            ) : (
              <>
                <CommonInput
                  id="worker"
                  name="workerId"
                  label="Worker *"
                  placeholder="Search by name or email"
                  value={formik.values.workerId}
                  onChange={event => formik.setFieldValue('workerId', event.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: '7px',
                    border: '1px solid #ccc',
                    boxSizing: 'border-box',
                  }}
                />
                <FormErrorWrap errorMessage={formik.errors.workerId as string} visible={!!formik.errors.workerId} />
              </>
            )}
          </FormInputWrapper>

          <ModuleLine style={{ display: 'flex', gap: '36px', marginBottom: '-10px' }}>
            <FormInputWrapper style={{ flex: '1', maxWidth: '108%' }}>
              <CommonDropdown
                label="Assignment *"
                id="assignmentId"
                name="assignmentId"
                options={assignmentOptionsWithBlank}
                value={formik.values.assignmentId}
                setValue={handleAssignmentChange}
                selectStyle={{
                  width: '100%',
                  height: '35px',
                  borderRadius: '7px',
                  border: '1px solid #ccc',
                }}
              />
              <FormErrorWrap errorMessage={formik.errors.assignmentId as string} visible={showError('assignmentId')} />
            </FormInputWrapper>

            <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
              <DatePicker
                id="invoiceDate"
                name="invoiceDate"
                label="Invoice Date *"
                dateFormat="MM/dd/yyyy"
                maxDate={new Date()}
                selected={formik.values.invoiceDate ?? null}
                onChange={(val: Date | null) => formik.setFieldValue('invoiceDate', val)}
                onBlur={() => formik.setFieldTouched('invoiceDate', true)}
                autoComplete="off"
              />
              <FormErrorWrap errorMessage={formik.errors.invoiceDate as string} visible={showError('invoiceDate')} />
            </FormInputWrapper>

            <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
              <CommonInput
                id="invoiceNumber"
                name="invoiceNumber"
                label="Invoice Number"
                placeholder="External Reference ID"
                value={formik.values.invoiceNumber}
                onChange={event => formik.setFieldValue('invoiceNumber', event.target.value)}
                autoComplete="off"
                style={{
                  width: '100%',
                  borderRadius: '7px',
                  border: '1px solid #ccc',
                  boxSizing: 'border-box',
                }}
              />
            </FormInputWrapper>
          </ModuleLine>

          <ModuleLine style={{ display: 'flex', gap: '36px', marginBottom: '-10px' }}>
            <FormInputWrapper style={{ flex: '1', maxWidth: '108%' }}>
              <DatePicker
                id="periodStart"
                name="periodStart"
                label="Period Start *"
                dateFormat="MM/dd/yyyy"
                selected={formik.values.periodStart ?? null}
                onChange={(val: Date | null) => formik.setFieldValue('periodStart', val)}
                autoComplete="off"
                // disabled={!allowEditDelete}
                minDate={contractStartDate ?? undefined}
                maxDate={effectiveMaxDate}
              />
              <FormErrorWrap errorMessage={formik.errors.periodStart as string} visible={showError('periodStart')} />
            </FormInputWrapper>

            <FormInputWrapper style={{ flex: '1', maxWidth: '108%' }}>
              <DatePicker
                id="periodEnd"
                name="periodEnd"
                label="Period End *"
                dateFormat="MM/dd/yyyy"
                selected={formik.values.periodEnd ?? null}
                onChange={(val: Date | null) => formik.setFieldValue('periodEnd', val)}
                autoComplete="off"
                // disabled={!allowEditDelete}
                minDate={formik.values.periodStart ?? contractStartDate ?? undefined}
                maxDate={effectiveMaxDate}
              />
              <FormErrorWrap errorMessage={formik.errors.periodEnd as string} visible={showError('periodEnd')} />
            </FormInputWrapper>
          </ModuleLine>

          <ModuleLine style={{ display: 'flex', gap: '36px', marginBottom: '-10px' }}>
            <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
              <CommonInput
                id="invoiceAmount"
                name="invoiceAmount"
                label="Invoice Amount *"
                placeholder="Money"
                value={formik.values.invoiceAmount}
                onChange={event => formik.setFieldValue('invoiceAmount', event.target.value)}
                autoComplete="off"
                style={{
                  width: '100%',
                  borderRadius: '7px',
                  border: '1px solid #ccc',
                  boxSizing: 'border-box',
                }}
              />
              <FormErrorWrap
                errorMessage={formik.errors.invoiceAmount as string}
                visible={showError('invoiceAmount')}
              />
            </FormInputWrapper>

            <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
              <CommonInput
                id="totalUnits"
                name="totalUnits"
                label="Total Units/Hours *"
                placeholder="Number"
                value={formik.values.totalUnits}
                onChange={event => formik.setFieldValue('totalUnits', event.target.value)}
                autoComplete="off"
                style={{
                  width: '100%',
                  borderRadius: '7px',
                  border: '1px solid #ccc',
                  boxSizing: 'border-box',
                }}
              />
              <FormErrorWrap errorMessage={formik.errors.totalUnits as string} visible={showError('totalUnits')} />
            </FormInputWrapper>
          </ModuleLine>

          <FormInputWrapper style={{ flex: '0 0 100%', marginBottom: '5px', position: 'relative' }}>
            <label
              htmlFor="fileUpload"
              style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '16px',
                color: '#222247',
              }}
            >
              Attachments *
            </label>
            <input
              ref={fileInputRef}
              id="fileUpload"
              name="Attachment"
              type="file"
              style={{
                display: 'none',
              }}
              onChange={handleFileChange}
            />
            <div
              style={{
                width: '100%',
                height: '136px',
                border: '1px solid #ccc',
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                marginBottom: '0px',
                boxSizing: 'border-box',
              }}
              onClick={handleFileUploadClick}
              onDragOver={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onDrop={handleFileDrop}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  font: '18px/27px Biotif',
                }}
              >
                <span
                  style={{
                    color: '#4A4BA9',
                    fontWeight: 'bold',
                    marginRight: '5px',
                  }}
                >
                  Add files
                </span>
                <span
                  style={{
                    color: '#222247',
                    opacity: 0.5,
                  }}
                >
                  Or drop files here
                </span>
              </div>
            </div>
            <FormErrorWrap errorMessage={formik.errors.attachments as string} visible={showError('attachments')} />
          </FormInputWrapper>

          <FormInputWrapper style={{ flex: '0 0 100%', marginTop: '0px', marginBottom: '20px' }}>
            <div
              style={{
                width: '55%',
                marginTop: '0px',
                paddingTop: '0px',
                borderRadius: '7px',
              }}
            >
              {uploadedFiles.map((file, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginTop: '0px' }}>
                  <a
                    href={URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#4A4BA9',
                      textDecoration: 'none',
                      marginRight: '10px',
                      fontSize: '12px',
                    }}
                  >
                    {file?.name}
                  </a>
                  <span
                    onClick={() => handleRemoveFile(index)}
                    style={{
                      color: '#4A4BA9',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          </FormInputWrapper>

          <FormInputWrapper style={{ flex: '0 0 100%', marginBottom: '-10px' }}>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '5px' }}>
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              placeholder="Notes"
              value={formik.values.notes}
              onChange={event => formik.setFieldValue('notes', event.target.value)}
              style={{
                width: '100%',
                height: '136px',
                borderRadius: '7px',
                border: '1px solid #ccc',
                boxSizing: 'border-box',
                padding: '10px',
                resize: 'none',
                fontSize: '14px',
                textAlign: 'left',
                verticalAlign: 'top',
              }}
            />
            <FormErrorWrap
              errorMessage={formik.errors.notes as string}
              visible={!!(formik.touched.notes && formik.errors.notes)}
            />
          </FormInputWrapper>

          <FormInputWrapper
            style={{ flex: '0 0 100%', display: 'flex', justifyContent: 'flex-start', marginBottom: '5px' }}
          >
            <OutlinedButton onClick={() => navigate('/invoices')}>Cancel</OutlinedButton>

            <OutlinedButton
              onClick={() => {
                formik.setFieldValue('status', 'Draft');
                formik.handleSubmit();
              }}
              disabled={formik.isSubmitting || addInvoice.isLoading}
            >
              Draft
            </OutlinedButton>

            <RegularButton
              onClick={() => {
                formik.setFieldValue('status', 'Submitted');
                formik.handleSubmit();
              }}
              disabled={formik.isSubmitting || addInvoice.isLoading}
            >
              Submit
            </RegularButton>
          </FormInputWrapper>
        </HelpForm>
      </CardContainer>
    </Container>
  );
};

export default CreateInvoice;
